/**
 * `/settings/accounts` (design §2.9) — the real thing.
 *
 * The design's non-negotiables, all honoured here:
 *  - "Sign in with Google" (identity) is NOT "Connect Gmail" (data) — stated in
 *    copy, because getting it wrong is how a user believes mail is syncing when
 *    only their login is.
 *  - Adding a SECOND account of the same provider must be obviously possible:
 *    the connect buttons are always present; the UI is a list, not a toggle.
 *  - Disconnect states plainly that history is retained (the server keeps it).
 *
 * ## `?reconnect=<accountId>` — the one-tap landing
 *
 * The reauth push (owner decision 2026-08-29) deep-links here. On mount, if the
 * named account exists and still needs reauth, the OAuth flow starts
 * IMMEDIATELY with `loginHint` set to its address — tap the push, tap the
 * provider's confirm, done. Guarded so a stale link (already reconnected, or
 * someone else's account id) lands on the normal page instead of bouncing the
 * browser to a consent screen nobody asked for.
 */

import { Badge, Button, Card, CardContent, CardHeader, CardTitle, EmptyState } from '@crm/ui';
import { createFileRoute } from '@tanstack/react-router';
import { PlugIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import {
  type ConnectedAccountRow,
  useConnectedAccountsQuery,
  useConnectMutation,
  useDisconnectMutation,
  useMailToggleMutation,
} from '../../../api/accounts.ts';
import { RouteErrorPanel } from '../../../components/route-error-boundary.tsx';
import { formatDate } from '../../../lib/format.ts';

const searchSchema = z.object({
  reconnect: z.string().uuid().optional(),
});

export const Route = createFileRoute('/_authed/settings/accounts')({
  component: AccountsSettings,
  errorComponent: RouteErrorPanel,
  validateSearch: searchSchema,
});

const PROVIDER_LABEL: Record<ConnectedAccountRow['provider'], string> = {
  google: 'Google',
  microsoft: 'Microsoft',
};

function healthBadge(account: ConnectedAccountRow) {
  switch (account.status) {
    case 'active':
      return <Badge variant="default">syncing</Badge>;
    case 'reauth_required':
      return <Badge variant="destructive">needs reconnect</Badge>;
    case 'revoked':
      return <Badge variant="outline">disconnected</Badge>;
    case 'paused':
      return <Badge variant="outline">paused</Badge>;
    default:
      return <Badge variant="destructive">{account.status}</Badge>;
  }
}

function AccountsSettings() {
  const { reconnect } = Route.useSearch();
  const accounts = useConnectedAccountsQuery();
  const connect = useConnectMutation();
  const disconnect = useDisconnectMutation();
  const mailToggle = useMailToggleMutation();
  const [confirmingDisconnect, setConfirmingDisconnect] = useState<string | null>(null);

  // One-tap reconnect from the push deep link. The ref makes it fire at most
  // once per mount — a connect error must not retry a navigation loop.
  const reconnectFired = useRef(false);
  useEffect(() => {
    if (reconnect === undefined || reconnectFired.current) return;
    const target = accounts.data?.items.find((row) => row.id === reconnect);
    if (target === undefined || target.status !== 'reauth_required') return;
    reconnectFired.current = true;
    connect.mutate({ provider: target.provider, loginHint: target.email });
  }, [reconnect, accounts.data, connect]);

  const items = accounts.data?.items ?? [];

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Connected accounts</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <p className="text-muted-foreground text-sm">
            Connecting an account is how calendar and mail reach your timeline. Signing in with
            Google is a different thing — it authenticates you and syncs nothing. You can connect
            several accounts per provider.
          </p>
          <div className="flex gap-2">
            <Button
              onClick={() => connect.mutate({ provider: 'google' })}
              disabled={connect.isPending}
            >
              Connect Google
            </Button>
            <Button
              onClick={() => connect.mutate({ provider: 'microsoft' })}
              disabled={connect.isPending}
              variant="outline"
            >
              Connect Microsoft
            </Button>
          </div>
          {connect.isError ? (
            <p className="text-destructive text-sm">
              {connect.error instanceof Error ? connect.error.message : 'Could not start connect'}
            </p>
          ) : null}
        </CardContent>
      </Card>

      {accounts.isLoading ? null : items.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              icon={<PlugIcon aria-hidden />}
              title="No accounts connected"
              description="Connect Google or Microsoft above to start syncing calendar and mail."
            />
          </CardContent>
        </Card>
      ) : (
        items.map((account) => (
          <Card key={account.id} data-testid={`account-${account.id}`}>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">
                {PROVIDER_LABEL[account.provider]} — {account.email}
              </CardTitle>
              {healthBadge(account)}
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <dl className="text-muted-foreground grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <dt>Calendar sync</dt>
                <dd>{account.calendarSyncEnabled ? 'on' : 'off'}</dd>
                <dt>Mail sync</dt>
                <dd>{account.mailSyncEnabled ? 'on' : 'off'}</dd>
                <dt>Connected</dt>
                <dd>{formatDate(account.connectedAt)}</dd>
                {account.lastError !== null && account.status !== 'active' ? (
                  <>
                    <dt>Last error</dt>
                    <dd className="truncate" title={account.lastError}>
                      {account.lastError}
                    </dd>
                  </>
                ) : null}
              </dl>

              <div className="flex flex-wrap gap-2">
                {account.status === 'reauth_required' || account.status === 'revoked' ? (
                  <Button
                    size="sm"
                    onClick={() =>
                      connect.mutate({ provider: account.provider, loginHint: account.email })
                    }
                    disabled={connect.isPending}
                  >
                    Reconnect
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    mailToggle.mutate({ accountId: account.id, enabled: !account.mailSyncEnabled })
                  }
                  disabled={mailToggle.isPending || account.status !== 'active'}
                >
                  {account.mailSyncEnabled ? 'Turn mail sync off' : 'Turn mail sync on'}
                </Button>
                {confirmingDisconnect === account.id ? (
                  <>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => {
                        disconnect.mutate(account.id);
                        setConfirmingDisconnect(null);
                      }}
                    >
                      Disconnect — history is kept
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmingDisconnect(null)}>
                      Cancel
                    </Button>
                  </>
                ) : account.status !== 'revoked' ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirmingDisconnect(account.id)}
                  >
                    Disconnect…
                  </Button>
                ) : null}
              </div>
              {mailToggle.data !== undefined &&
              mailToggle.data.accountId === account.id &&
              !mailToggle.data.queued &&
              mailToggle.data.mailSyncEnabled ? (
                <p className="text-muted-foreground text-sm">
                  Mail sync is on, but nothing was queued:{' '}
                  {mailToggle.data.reason ?? 'the mail lane is not configured on this deployment'}.
                </p>
              ) : null}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
